import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import retreatService from '../services/retreatService';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const RetreatSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');

  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (centerId) fetchCamps();
  }, [centerId]);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      const data = await retreatService.getCampsByCenter(centerId);
      setCamps(data || []);
      setError(null);
    } catch (err) {
      setError('Failed to load retreats. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    if (selectedCamp) {
      navigate(`/retreat/registration?campId=${selectedCamp.camp_Id}&centerId=${centerId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/retreat/center')}
            className="text-blue-600 hover:text-blue-800 mb-6 font-semibold flex items-center gap-1 mx-auto bg-transparent border-none cursor-pointer"
          >
            ← Back to Centers
          </button>
          <span className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            🧘 Retreat Programs
          </span>
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Select Your Retreat
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose a retreat program to begin registration
          </p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading retreats...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 max-w-2xl mx-auto">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchCamps}
              className="mt-2 text-sm text-red-600 underline hover:text-red-800 bg-transparent border-none cursor-pointer">
              Retry
            </button>
          </div>
        )}

        {!loading && camps.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🧘</div>
            <p className="text-gray-600 text-lg">No retreats available for this center.</p>
          </div>
        )}

        {!loading && camps.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {camps.map((camp) => {
                const isSelected = selectedCamp?.camp_Id === camp.camp_Id;
                return (
                  <div
                    key={camp.camp_Id}
                    onClick={() => setSelectedCamp(camp)}
                    className={`bg-white rounded-2xl p-6 shadow-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      isSelected
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-transparent hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {isSelected && (
                            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">Selected ✓</span>
                          )}
                          <h3 className="text-xl font-bold text-gray-900">{camp.camp_Name}</h3>
                        </div>
                      </div>
                      <div className="text-right ml-4 flex-shrink-0">
                        {camp.camp_Fee > 0 ? (
                          <span className="text-2xl font-bold text-blue-600">${camp.camp_Fee}</span>
                        ) : (
                          <span className="bg-green-100 text-green-700 font-bold px-3 py-1 rounded-full text-sm">Free</span>
                        )}
                      </div>
                    </div>

                    {camp.camp_Description && (
                      <div
                        className="text-gray-600 text-sm mb-4 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: camp.camp_Description }}
                      />
                    )}

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-gray-500 text-xs mb-1">Start Date</p>
                        <p className="font-semibold text-gray-800">{fmtDate(camp.camp_Date_From)}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3">
                        <p className="text-gray-500 text-xs mb-1">End Date</p>
                        <p className="font-semibold text-gray-800">{fmtDate(camp.camp_Date_To)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary Panel */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4 border border-gray-100">
                {selectedCamp ? (
                  <>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Retreat Summary</h3>
                    <div className="space-y-3 mb-6">
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide">Retreat</p>
                        <p className="font-semibold text-gray-800">{selectedCamp.camp_Name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase tracking-wide">Duration</p>
                        <p className="font-semibold text-gray-800">
                          {fmtDate(selectedCamp.camp_Date_From)} – {fmtDate(selectedCamp.camp_Date_To)}
                        </p>
                      </div>
                      <div className="border-t pt-3">
                        <p className="text-gray-500 text-xs uppercase tracking-wide">Fee</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {selectedCamp.camp_Fee > 0 ? `$${selectedCamp.camp_Fee}` : 'Free'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleProceed}
                      className="w-full text-white font-semibold py-3 rounded-xl border-none cursor-pointer transition hover:shadow-lg"
                      style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                    >
                      Proceed to Registration →
                    </button>
                  </>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    <div className="text-4xl mb-3">🧘</div>
                    <p className="text-sm">Select a retreat to continue</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetreatSelection;
